import BpkBadge, {
  BADGE_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-badge';
import ChevronRightIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/chevron-right';
import BpkText, {
  TEXT_STYLES,
  TEXT_COLORS,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import withErrorBoundary from '../ErrorBoundary';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  CarHireCheapestLocationLine,
  CarHireCheapestLocationProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/CarHireCheapestLocationProps';

import STYLES from './CarHireCheapestLocation.module.scss';

const getRatio = (price: number, maxPrice: number) => {
  if (!maxPrice || price > maxPrice) {
    return 100;
  }
  return (price / maxPrice) * 100;
};

const Line = ({
  buildMBDParam,
  isCheapest,
  line,
  maxPrice,
  mbdComponentName,
}: {
  isCheapest: boolean;
  line: CarHireCheapestLocationLine;
  maxPrice: number;
  mbdComponentName: string;
  buildMBDParam: (componentName: string) => string;
}) => {
  const { city, formattedPrice, price, url } = line;
  const ratio = getRatio(price, maxPrice);
  const labelStyle = isCheapest ? STYLES.Label_cheapest : STYLES.Label_regular;
  const labelTextColor = isCheapest
    ? TEXT_COLORS.textOnDark
    : TEXT_COLORS.textPrimary;

  return (
    <a
      href={url}
      className={STYLES.Line}
      data-tracking-element-id={mbdComponentName}
      data-tracking-common-params={buildMBDParam(mbdComponentName)}
    >
      <div className={STYLES.Track}>
        <div className={labelStyle} style={{ width: `${ratio}%` }}>
          <BpkText
            textStyle={TEXT_STYLES.label3}
            color={labelTextColor}
            className={STYLES.Label__text}
          >
            {city}
          </BpkText>
        </div>
      </div>
      <BpkBadge
        type={isCheapest ? BADGE_TYPES.brand : BADGE_TYPES.strong}
        className={STYLES.Badge}
      >
        {formattedPrice}
        <ChevronRightIcon className={STYLES.Badge__chevron} />
      </BpkBadge>
    </a>
  );
};

const CarHireCheapestLocation = (props: CarHireCheapestLocationProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!props.lines || props.lines.length === 0) {
    return null;
  }

  const { description, headline, isMobile, lines, rtl } = props;

  const prices = lines.map((line) => line.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  const className = classNames(STYLES.CarHireCheapestLocation, {
    [STYLES.rtl]: rtl,
  });

  return (
    <div
      className={className}
      data-testid="carHireCheapestLocation"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <div className={STYLES.Intro}>
        <BpkText
          tagName="h2"
          textStyle={isMobile ? TEXT_STYLES.heading3 : TEXT_STYLES.heading2}
        >
          {headline}
        </BpkText>
        <BpkText tagName="p" textStyle={TEXT_STYLES.bodyDefault}>
          {description}
        </BpkText>
      </div>
      <div className={STYLES.Container}>
        {lines.map((line, index) => {
          const isCheapest = line.price === minPrice;
          return (
            <Line
              key={line.city}
              line={line}
              maxPrice={maxPrice}
              isCheapest={isCheapest}
              mbdComponentName={`${rawComponentName}_location_${index}`}
              buildMBDParam={buildMBDParam}
            />
          );
        })}
      </div>
    </div>
  );
};

const CarHireCheapestLocationWithErrorBoundary = withErrorBoundary(
  CarHireCheapestLocation,
  'car-hire-cheapest-location-component',
);

export default CarHireCheapestLocationWithErrorBoundary;
